import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  fetchState, fetchTemplateHtml, saveTemplateHtml,
  seedLibrary, resetRotation, toggleTemplate, sendTestEmail,
  MsTemplate, MsState,
} from '../../services/utilMemberStandards';

// ── Category styling ─────────────────────────────────────────────────────────

const CAT_STYLE: Record<string, { bg: string; color: string }> = {
  'ONE FIRM':     { bg: '#003D4C', color: '#fff' },
  'PROFESSIONAL': { bg: '#1e4d8c', color: '#fff' },
  'PERSONAL':     { bg: '#2e7d32', color: '#fff' },
  'SUPERVISORY':  { bg: '#b5460f', color: '#fff' },
  'ORIGINAL':     { bg: '#64748b', color: '#fff' },
};

function catStyle(cat: string) {
  return CAT_STYLE[cat] ?? { bg: '#475569', color: '#fff' };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function lastSentLabel(log: string[]): string {
  if (!log.length) return 'No emails sent yet this cycle';
  const last = log[log.length - 1];
  return `Last sent: ${last}`;
}

function cycleProgress(config: MsTemplate[], log: string[]): string {
  const active = config.filter(t => t.active);
  const sentThisCycle = log.filter(f => active.some(t => t.filename === f));
  return `${sentThisCycle.length} / ${active.length} active templates sent this cycle`;
}

// ── Toggle switch ─────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', cursor: disabled ? 'not-allowed' : 'pointer', gap: 8 }}>
      <div
        onClick={() => !disabled && onChange(!checked)}
        style={{
          width: 40, height: 22, borderRadius: 11,
          background: checked ? '#003D4C' : '#cbd5e1',
          position: 'relative', transition: 'background .2s',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: checked ? 21 : 3,
          width: 16, height: 16, borderRadius: '50%',
          background: '#fff', transition: 'left .2s',
          boxShadow: '0 1px 3px rgba(0,0,0,.3)',
        }} />
      </div>
    </label>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────────────

interface EditModalProps {
  template: MsTemplate;
  onClose: () => void;
  onSaved: () => void;
}

function EditModal({ template, onClose, onSaved }: EditModalProps) {
  const [html, setHtml]     = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    fetchTemplateHtml(template.filename)
      .then(h => { setHtml(h); setLoading(false); })
      .catch(() => { toast.error('Failed to load template HTML'); setLoading(false); });
  }, [template.filename]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveTemplateHtml(template.filename, html);
      toast.success('Template saved');
      onSaved();
      onClose();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const cs = catStyle(template.category);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,.55)', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        background: '#1e293b', color: '#f1f5f9',
        padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
      }}>
        <span style={{
          background: cs.bg, color: cs.color,
          fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
          textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4,
        }}>{template.category}</span>
        <span style={{ fontWeight: 600, fontSize: 15, flex: 1 }}>{template.title}</span>
        <button
          onClick={handleSave}
          disabled={saving || loading}
          style={{
            background: '#003D4C', color: '#fff', border: 'none',
            padding: '7px 18px', borderRadius: 6, fontWeight: 600,
            cursor: saving || loading ? 'not-allowed' : 'pointer', opacity: saving || loading ? 0.6 : 1,
          }}
        >{saving ? 'Saving…' : 'Save'}</button>
        <button
          onClick={onClose}
          style={{
            background: 'transparent', color: '#94a3b8', border: '1px solid #334155',
            padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
          }}
        >Cancel</button>
      </div>

      {/* Body: editor + preview */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', background: '#0f172a' }}>
          Loading…
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* HTML editor */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid #334155', background: '#0f172a' }}>
            <div style={{ padding: '6px 12px', background: '#1e293b', fontSize: 11, color: '#64748b', fontFamily: 'monospace', letterSpacing: 1 }}>
              HTML SOURCE
            </div>
            <textarea
              value={html}
              onChange={e => setHtml(e.target.value)}
              spellCheck={false}
              style={{
                flex: 1, resize: 'none', border: 'none', outline: 'none',
                background: '#0f172a', color: '#e2e8f0',
                fontFamily: "'Consolas','Courier New',monospace", fontSize: 12,
                padding: '14px 16px', lineHeight: 1.6,
              }}
            />
          </div>
          {/* Live preview */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
            <div style={{ padding: '6px 12px', background: '#e2e8f0', fontSize: 11, color: '#64748b', letterSpacing: 1, fontFamily: 'monospace' }}>
              LIVE PREVIEW
            </div>
            <iframe
              ref={iframeRef}
              srcDoc={html}
              sandbox="allow-same-origin"
              style={{ flex: 1, border: 'none', width: '100%', background: '#fff' }}
              title="Template preview"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Test email dialog ─────────────────────────────────────────────────────────

interface TestDialogProps {
  template: MsTemplate;
  onClose: () => void;
}

function TestDialog({ template, onClose }: TestDialogProps) {
  const [to, setTo]       = useState('');
  const [sending, setSend] = useState(false);

  const handleSend = async () => {
    setSend(true);
    try {
      const result = await sendTestEmail(template.filename, to || undefined);
      toast.success(`Test email sent to ${result.sentTo}`);
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Send failed');
    } finally {
      setSend(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 900,
      background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 10, padding: 28, width: 420,
          boxShadow: '0 8px 32px rgba(0,0,0,.25)',
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Send Test Email</div>
        <div style={{ color: '#475569', fontSize: 13, marginBottom: 20 }}>{template.title}</div>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
          Recipient (leave blank to send to yourself)
        </label>
        <input
          type="email"
          placeholder="someone@bmss.com"
          value={to}
          onChange={e => setTo(e.target.value)}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: '1px solid #d1d5db', borderRadius: 6,
            padding: '8px 12px', fontSize: 14, marginBottom: 20,
          }}
        />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            style={{
              padding: '8px 18px', borderRadius: 6, border: 'none',
              background: '#003D4C', color: '#fff', fontWeight: 600,
              cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.6 : 1,
            }}
          >{sending ? 'Sending…' : 'Send Test'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function UtilityMemberStandardsPage() {
  const { isAdmin } = usePermissions();

  const [state, setState]       = useState<MsState | null>(null);
  const [loading, setLoading]   = useState(true);
  const [seeding, setSeeding]   = useState(false);
  const [resetting, setResetting] = useState(false);
  const [toggling, setToggling]   = useState<string | null>(null);

  const [editTarget, setEditTarget]   = useState<MsTemplate | null>(null);
  const [testTarget, setTestTarget]   = useState<MsTemplate | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await fetchState();
      setState(s);
    } catch {
      toast.error('Failed to load member standards state');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const result = await seedLibrary();
      toast.success(`Library initialized: ${result.created} templates (${result.active} active, ${result.inactive} inactive)`);
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset the rotation log? The cycle will restart from the first active template next Monday.')) return;
    setResetting(true);
    try {
      await resetRotation();
      toast.success('Rotation reset — cycle restarts next Monday');
      await load();
    } catch {
      toast.error('Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const handleToggle = async (t: MsTemplate, newActive: boolean) => {
    if (!isAdmin) return;
    setToggling(t.filename);
    try {
      await toggleTemplate(t.filename, newActive);
      setState(prev => prev ? {
        ...prev,
        config: prev.config.map(x => x.filename === t.filename ? { ...x, active: newActive } : x),
      } : prev);
      toast.success(`"${t.title}" ${newActive ? 'activated' : 'deactivated'}`);
    } catch (e: any) {
      toast.error(e?.message || 'Toggle failed');
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return <div style={{ padding: 32, color: '#64748b' }}>Loading…</div>;
  }

  const uninitialized = state?.config.length === 0;
  const config  = state?.config ?? [];
  const log     = state?.log ?? [];
  const active  = config.filter(t => t.active);

  // Group by source for display order
  const originals  = config.filter(t => t.source === 'original');
  const narratives = config.filter(t => t.source === 'narratives-2026');
  const others     = config.filter(t => t.source !== 'original' && t.source !== 'narratives-2026');
  const sorted     = [...originals, ...narratives, ...others];

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100, fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Member Standards Emails</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
            Manage the weekly Monday rotation sent to allmembers@bmss.com
          </p>
        </div>
        {isAdmin && !uninitialized && (
          <button
            onClick={handleReset}
            disabled={resetting}
            style={{
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7,
              padding: '8px 16px', fontSize: 13, color: '#475569', cursor: resetting ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
          >{resetting ? 'Resetting…' : 'Reset Rotation'}</button>
        )}
      </div>

      {/* Not initialized state */}
      {uninitialized && (
        <div style={{
          background: '#fefce8', border: '1px solid #fde68a', borderRadius: 10,
          padding: '24px 28px', marginBottom: 28,
        }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#854d0e', marginBottom: 6 }}>
            Library not initialized
          </div>
          <p style={{ color: '#713f12', fontSize: 14, margin: '0 0 16px' }}>
            The template library hasn't been set up yet. Click below to import the 20 existing templates and add
            the 20 new narratives (inactive by default) from the July 2026 Word document.
          </p>
          {isAdmin ? (
            <button
              onClick={handleSeed}
              disabled={seeding}
              style={{
                background: '#003D4C', color: '#fff', border: 'none',
                padding: '10px 22px', borderRadius: 7, fontWeight: 700,
                cursor: seeding ? 'not-allowed' : 'pointer', opacity: seeding ? 0.7 : 1, fontSize: 14,
              }}
            >{seeding ? 'Initializing…' : 'Initialize Library'}</button>
          ) : (
            <p style={{ color: '#92400e', fontSize: 13 }}>An admin needs to initialize the library first.</p>
          )}
        </div>
      )}

      {/* Rotation status bar */}
      {!uninitialized && (
        <div style={{
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8,
          padding: '12px 18px', marginBottom: 24, display: 'flex', gap: 24, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 13, color: '#0369a1', fontWeight: 600 }}>
            {cycleProgress(config, log)}
          </span>
          <span style={{ fontSize: 13, color: '#475569' }}>
            {lastSentLabel(log)}
          </span>
          <span style={{ fontSize: 13, color: '#475569' }}>
            {active.length} of {config.length} templates active
          </span>
        </div>
      )}

      {/* Template table */}
      {!uninitialized && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Category</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Title</th>
                <th style={thStyle}>In Rotation</th>
                <th style={thStyle}>Log Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t, i) => {
                const inLog    = log.includes(t.filename);
                const isToggling = toggling === t.filename;
                const cs       = catStyle(t.category);
                const isNew    = t.source === 'narratives-2026';

                return (
                  <tr
                    key={t.filename}
                    style={{
                      borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
                      background: t.active ? '#fff' : '#f8fafc',
                    }}
                  >
                    {/* # */}
                    <td style={tdCenter}>
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>{t.id}</span>
                    </td>

                    {/* Category */}
                    <td style={tdCenter}>
                      <span style={{
                        background: cs.bg, color: cs.color,
                        fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
                        textTransform: 'uppercase', padding: '3px 7px', borderRadius: 4,
                        whiteSpace: 'nowrap',
                      }}>{t.category}</span>
                    </td>

                    {/* Title */}
                    <td style={{ ...tdBase, textAlign: 'left' }}>
                      <span style={{ fontSize: 14, color: t.active ? '#0f172a' : '#64748b', fontWeight: 500 }}>
                        {t.title}
                      </span>
                      {isNew && (
                        <span style={{
                          marginLeft: 8, background: '#dcfce7', color: '#166534',
                          fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                        }}>NEW</span>
                      )}
                    </td>

                    {/* Active toggle */}
                    <td style={tdCenter}>
                      <Toggle
                        checked={t.active}
                        onChange={v => handleToggle(t, v)}
                        disabled={!isAdmin || isToggling}
                      />
                    </td>

                    {/* Log status */}
                    <td style={tdCenter}>
                      {t.active ? (
                        inLog ? (
                          <span style={{ fontSize: 12, color: '#64748b', background: '#f1f5f9', padding: '3px 8px', borderRadius: 12 }}>
                            ✓ Sent
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: '#16a34a', background: '#dcfce7', padding: '3px 8px', borderRadius: 12, fontWeight: 600 }}>
                            Pending
                          </span>
                        )
                      ) : (
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td style={tdCenter}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        <ActionBtn onClick={() => setTestTarget(t)} label="Test" color="#0369a1" />
                        <ActionBtn onClick={() => setEditTarget(t)} label="Edit" color="#374151" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <EditModal
          template={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={load}
        />
      )}

      {/* Test dialog */}
      {testTarget && (
        <TestDialog
          template={testTarget}
          onClose={() => setTestTarget(null)}
        />
      )}
    </div>
  );
}

// ── Small style helpers ───────────────────────────────────────────────────────

const tdBase: React.CSSProperties = {
  padding: '12px 14px', verticalAlign: 'middle', fontSize: 13,
};
const tdCenter: React.CSSProperties = { ...tdBase, textAlign: 'center' };

const thStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 11, fontWeight: 700,
  color: '#64748b', textAlign: 'center', letterSpacing: '0.5px',
  textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0',
};

function ActionBtn({ onClick, label, color }: { onClick: () => void; label: string; color: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'transparent', border: `1px solid ${color}`,
        color, padding: '4px 10px', borderRadius: 5,
        fontSize: 12, fontWeight: 600, cursor: 'pointer',
      }}
    >{label}</button>
  );
}
